import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { buildCreateArtworkUrl } from '@hooks/useArtworkBuilderPaths'
import useOrderItem from '@hooks/useOrderItem'
import useRedirectUrlWithCacheKey from '@hooks/useRedirectUrlWithCacheKey'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import type { ArtworkModel, OrderItemModel } from '@models/types'

import Artwork from './components/Artwork'

const MODAL_KEY = 'DuplicateOrderItemArtworkModal'

type ModalPayload = {
  existingArtworks: ArtworkModel[],
  orderItem: OrderItemModel,
}

type ModalCallbacks = {
  closeModal: () => void,
  selectExistingArtwork: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  requestOptions?: object,
  showModal: boolean,
}

const DuplicateOrderItemArtworkModal = (props: ModalProps) => {
  const { callbacks, requestOptions, showModal } = props
  const { closeModal, selectExistingArtwork } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload: ModalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    existingArtworks = [],
    orderItem: initOrderItem,
  } = modalPayload

  const {
    callbacks: {
      assignExistingArtwork: assignFn,
    },
    creating,
    orderItem,
  } = useOrderItem(initOrderItem)

  const { redirectUrl } = useRedirectUrlWithCacheKey([orderItem.id])
  const createArtworkUrl = buildCreateArtworkUrl({ orderItem, redirectUrl })

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Duplicate" titleSecondLine="Artwork" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Use Existing Artwork">
            {existingArtworks.map(artwork => (
              <Artwork
                artwork={artwork}
                callbacks={{
                  selectArtwork: () => (
                    selectExistingArtwork({
                      callbacks: {
                        // @ts-expect-error TS2322
                        action: assignFn,
                        afterAction: closeModal,
                        buildActionParams: () => [
                          artwork,
                          requestOptions,
                        ],
                      },
                      toastText: 'Artwork Duplicated',
                    })
                  ),
                }}
                key={artwork.id}
              />
            ))}
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          // @ts-expect-error TS2322
          as="a"
          buttonStyle="primaryCreate"
          href={createArtworkUrl}
          loading={creating}
          size="large"
        >
          Start New Artwork
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <DuplicateOrderItemArtworkModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
